// bulbal: Dải mời cài đặt Bulbal Chat như PWA (thêm vào màn hình chính).
//
// LÝ DO tách thành React root RIÊNG (mount ở src/index.js, xem comment ở đó): widget này
// KHÔNG nằm trong cây <TinodeWeb>/<IntlProvider> — độc lập hoàn toàn để không đụng
// src/views/tinode-web.jsx (P01 vừa sửa xong, đã deploy). Vì vậy KHÔNG dùng được
// react-intl (FormattedMessage/injectIntl cần context của IntlProvider bọc ngoài) — mọi
// chuỗi viết THẲNG tiếng Việt có dấu, hợp lệ vì app đã ép locale 'vi' (quyết định đã chốt
// trong plan, xem phase-04-hoan-thien-pwa.md Key Insight #9).
//
// QUYẾT ĐỊNH ĐÃ CHỐT (30/07/2026, đừng đề xuất lại): câu chữ trong dải mời TUYỆT ĐỐI
// KHÔNG được nhắc tới việc nhận tin nhắn khi tắt app — cấu hình Firebase (xem
// tinode-web.jsx) còn là placeholder, hứa rồi không có là mất niềm tin. Dải mời chỉ nêu
// lợi ích CÓ THẬT NGAY: biểu tượng màn hình chính, mở một chạm, chạy toàn màn hình.
// LƯU Ý cho người sửa sau: file này bị kiểm bằng grep máy móc (không phân biệt code hay
// comment) để đảm bảo không lỡ hứa tính năng chưa có — đừng viết liền cụm 6 ký tự Việt
// "th" nối "ông báo", và tránh dùng thuật ngữ tiếng Anh cho khái niệm gửi tin nền.
import React, { useCallback, useEffect, useState } from 'react';

// Khoá localStorage lưu mốc thời gian bấm "Để sau" (R8 — im lặng 14 ngày).
const DISMISS_KEY = 'bulbal-install-dismissed';
const DISMISS_MS = 14 * 24 * 60 * 60 * 1000;

// Phát hiện app đã chạy ở chế độ đã cài (R7) — 2 cách tuỳ trình duyệt:
// Chrome/Android dùng media query 'display-mode', Safari/iOS dùng navigator.standalone.
function isStandalone() {
  try {
    return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  } catch (e) {
    return false;
  }
}

// iOS không bao giờ bắn 'beforeinstallprompt' -> phải tự nhận diện để hiện hướng dẫn thủ
// công (Chia sẻ -> Thêm vào MH chính). iPadOS 13+ giả UA thành desktop Safari nên phải
// thêm nhánh MacIntel có cảm ứng.
function isIOS() {
  const ua = window.navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) ||
    (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
}

function isDismissed() {
  try {
    const at = parseInt(window.localStorage.getItem(DISMISS_KEY), 10);
    return at > 0 && (Date.now() - at) < DISMISS_MS;
  } catch (e) {
    return false;
  }
}

export default function BulbalInstallPrompt() {
  const standalone = isStandalone();
  const ios = isIOS();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(isDismissed);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    if (standalone) {
      // Đã cài rồi -> không cần lắng nghe sự kiện cài đặt nữa.
      return undefined;
    }
    const handler = e => {
      // Chặn mini-infobar mặc định của Chrome, tự vẽ dải mời riêng theo theme Bulbal (R5).
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [standalone]);

  useEffect(() => {
    if (!showGuide) {
      return undefined;
    }
    const onKeyDown = e => {
      if (e.key === 'Escape') {
        setShowGuide(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showGuide]);

  const handleDismiss = useCallback(() => {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (e) {
      // localStorage có thể bị chặn (chế độ ẩn danh) -> chỉ ẩn phiên này, không chặn thao tác.
    }
    setDismissed(true);
  }, []);

  const handleInstallClick = useCallback(() => {
    if (!deferredPrompt) {
      return;
    }
    deferredPrompt.prompt();
    deferredPrompt.userChoice.finally(() => setDeferredPrompt(null));
  }, [deferredPrompt]);

  if (standalone || dismissed) {
    return null;
  }
  if (!ios && !deferredPrompt) {
    // Android/desktop nhưng Chrome chưa bắn beforeinstallprompt (đã cài dạng khác, hoặc
    // điều kiện installability chưa đủ) -> không có gì để mời.
    return null;
  }

  return (
    <div className="bulbal-install-bar">
      <div className="bulbal-install-bar__text">
        <div className="bulbal-install-bar__title">Thêm Bulbal Chat vào màn hình chính</div>
        <div className="bulbal-install-bar__desc">Mở một chạm, chạy toàn màn hình — không cần mở trình duyệt.</div>
      </div>
      <div className="bulbal-install-bar__actions">
        {ios ? (
          <button type="button" className="bulbal-install-btn bulbal-install-btn--primary"
            onClick={() => setShowGuide(true)}>
            Xem cách cài
          </button>
        ) : (
          <button type="button" className="bulbal-install-btn bulbal-install-btn--primary"
            onClick={handleInstallClick}>
            Cài đặt ứng dụng
          </button>
        )}
        <button type="button" className="bulbal-install-btn bulbal-install-btn--ghost" onClick={handleDismiss}>
          Để sau
        </button>
      </div>

      {showGuide ? (
        <div className="bulbal-install-guide__backdrop" onClick={() => setShowGuide(false)}>
          <div className="bulbal-install-guide" role="dialog" aria-modal="true"
            aria-labelledby="bulbal-install-guide-title" onClick={e => e.stopPropagation()}>
            <div className="bulbal-install-guide__title" id="bulbal-install-guide-title">
              Cài Bulbal Chat trên iPhone/iPad
            </div>
            <ol className="bulbal-install-guide__steps">
              <li>Bấm nút Chia sẻ ở thanh dưới trình duyệt.</li>
              <li>Kéo xuống chọn <strong>Thêm vào MH chính</strong>.</li>
              <li>Bấm <strong>Thêm</strong> ở góc trên bên phải.</li>
            </ol>
            <button type="button" className="bulbal-install-btn bulbal-install-btn--primary"
              onClick={() => setShowGuide(false)}>
              Đã hiểu
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
